import type { NewUserFormData } from "@/schemas/users/new-user";
import { formatPhoneNumber } from "@/utils/format";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Heading,
  Icon,
  Switch,
} from "@elaniintc/goes-design-system";
import { type ReactNode } from "react";
import { useFormContext } from "react-hook-form";

const IconWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <div className="size-7 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center text-body">
      {children}
    </div>
  );
};

const getRoleType = (type: string) => {
  switch (type) {
    case "editor":
      return "Editor";
    case "reader":
      return "Lector";
    case "admin":
      return "Administrador";
    default:
      return "";
  }
};

interface Props {
  selectedPosition: {
    label: string;
    value: string;
  } | null;
  selectedRole: {
    label: string;
    type: string;
  } | null;
  selectedOrganization: {
    label: string;
    value: string;
  } | null;
  selectedUnit: {
    label: string;
    value: string;
  } | null;
  isUserBlocked?: boolean;
  isUserActive?: boolean;
  toggleUserActivation: (isActive: boolean) => void;
  onResetPassword?: () => void;
}

const UserDetails = ({
  selectedPosition,
  selectedOrganization,
  selectedUnit,
  selectedRole,
  isUserActive,
  isUserBlocked = false,
  toggleUserActivation,
  onResetPassword,
}: Props) => {
  const { watch } = useFormContext<NewUserFormData>();
  const data = watch();

  return (
    <>
      <div className="p-4">
        <div className="flex flex-col gap-4 pb-4">
          <Avatar size="xl">
            <AvatarFallback color="action">
              {data.name?.charAt(0) || data.last_name?.charAt(0) || ""}
            </AvatarFallback>
          </Avatar>
          <div>
            <Heading
              variant="h5"
              className={cn(
                "text-on-disabled",
                (data.name || data.last_name) && "text-heading",
              )}
            >
              {data.name || data.last_name
                ? `${data.name} ${data.last_name}`
                : "Nombre y apellido"}
            </Heading>
            <p
              className={cn(
                "text-base leading-3xl text-body-quiet",
                selectedPosition && "text-on-disabled",
              )}
            >
              {selectedPosition?.label || "Puesto de trabajo"}
            </p>
          </div>
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="user-shield" />
            </IconWrapper>

            <span
              className={cn(
                "text-body font-normal",
                !selectedRole && "text-on-disabled",
              )}
              data-testid="profile-role"
            >
              {`${selectedRole?.label || "Rol de usuario"}`}
            </span>
          </div>
          {selectedRole?.type && getRoleType(selectedRole?.type) && (
            <Badge variant="neutral" size="sm">
              {getRoleType(selectedRole?.type)}
            </Badge>
          )}
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="flex flex-col gap-2 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconWrapper>
                <Icon type="far" name="id-card" />
              </IconWrapper>
              <span
                className={cn(
                  "text-body font-normal",
                  !data.dui && "text-on-disabled",
                )}
                data-testid="profile-dui"
              >
                {data.dui || "Número de DUI"}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconWrapper>
                <Icon type="far" name="envelope" />
              </IconWrapper>
              <span
                className={cn(
                  "text-body font-normal",
                  !data.email && "text-on-disabled",
                )}
                data-testid="profile-email"
              >
                {data.email || "Correo electrónico"}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconWrapper>
                <Icon type="far" name="phone" />
              </IconWrapper>
              <span
                className={cn(
                  "text-body font-normal",
                  !data.phone_number && "text-on-disabled",
                )}
                data-testid="profile-phone"
              >
                {data.phone_number
                  ? `(+503) ${formatPhoneNumber(data?.phone_number || "")}`
                  : "Número de teléfono"}
              </span>
            </div>
          </div>
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="flex flex-col gap-2 py-4">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="building" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !selectedUnit && "text-on-disabled",
              )}
              data-testid="profile-organization"
            >
              {selectedOrganization?.label || "Organización"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="users" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !selectedUnit && "text-on-disabled",
              )}
              data-testid="profile-department"
            >
              {selectedUnit?.label || "Unidad organizativa"}
            </span>
          </div>
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="flex flex-col gap-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconWrapper>
                <Icon type="far" name="user-check" />
              </IconWrapper>
              <span className="text-body-loud text-base leading-3xl font-semibold">
                Estado
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <label
                htmlFor="profile-status-switch"
                className="text-body text-base leading-3xl"
              >
                {isUserActive ? "Activo" : "Inactivo"}
              </label>
              <Switch
                checked={isUserActive}
                id="profile-status-switch"
                onCheckedChange={(checked) => {
                  toggleUserActivation(checked);
                }}
                data-testid="profile-status-switch"
                className="!border-none"
              />
            </div>
          </div>
          <Button
            size="default"
            variant="tertiary"
            className="w-full"
            type="button"
            onClick={onResetPassword}
          >
            Restablecer contraseña
            <Icon type="far" name="lock" className="text-current" />
          </Button>
          <Button
            size="default"
            variant="tertiary"
            className="w-full"
            type="button"
            disabled={!isUserBlocked}
          >
            Desbloquear usuario
            <Icon type="far" name="user-unlock" className="text-current" />
          </Button>
        </div>
      </div>
    </>
  );
};

export default UserDetails;
